'use client';

import React from 'react';

interface DualInputSliderProps {
  id?: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  prefix?: string;
  suffix?: string;
  helpText?: string;
  formatValue?: (value: number) => string;
  minLabel?: string;
  maxLabel?: string;
}

const SLIDER_MIN = 0;
const SLIDER_MAX = 100;
const SLIDER_STEP = 0.1;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getPrecision(step?: number) {
  if (!step || Number.isInteger(step)) {
    return 0;
  }
  const stepString = step.toString();
  if (!stepString.includes('.')) {
    return 0;
  }
  return stepString.split('.')[1]?.length ?? 0;
}

export function DualInputSlider({
  id,
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  prefix,
  suffix,
  helpText,
  formatValue,
  minLabel,
  maxLabel,
}: DualInputSliderProps) {
  const safeMin = Number.isFinite(min) ? min : 0;
  const safeMax = Number.isFinite(max) ? max : safeMin + 1;
  const precision = React.useMemo(() => getPrecision(step), [step]);
  const logOffset = React.useMemo(() => (safeMin <= 0 ? 1 - safeMin : 0), [safeMin]);

  const logMin = React.useMemo(() => {
    const adjusted = safeMin + logOffset;
    return Math.log10(adjusted <= 0 ? Number.EPSILON : adjusted);
  }, [safeMin, logOffset]);

  const logMax = React.useMemo(() => {
    const adjusted = safeMax + logOffset;
    return Math.log10(adjusted <= 0 ? Number.EPSILON : adjusted);
  }, [safeMax, logOffset]);

  const sliderValue = React.useMemo(() => {
    if (safeMax === safeMin) {
      return SLIDER_MIN;
    }

    const clampedValue = clamp(value, safeMin, safeMax);
    const adjusted = clampedValue + logOffset;
    const logAdjusted = Math.log10(adjusted <= 0 ? Number.EPSILON : adjusted);
    const ratio = (logAdjusted - logMin) / (logMax - logMin || 1);
    const sliderPosition = SLIDER_MIN + ratio * (SLIDER_MAX - SLIDER_MIN);
    return clamp(sliderPosition, SLIDER_MIN, SLIDER_MAX);
  }, [value, safeMin, safeMax, logOffset, logMin, logMax]);

  const formatDisplay = React.useCallback(
    (val: number) => {
      if (formatValue) {
        return formatValue(val);
      }

      const formatted = Number.isFinite(val)
        ? val.toLocaleString(undefined, {
            minimumFractionDigits: precision,
            maximumFractionDigits: precision,
          })
        : '0';

      return `${prefix ?? ''}${formatted}${suffix ?? ''}`;
    },
    [formatValue, prefix, suffix, precision]
  );

  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSliderValue = Number(event.target.value);
    if (Number.isNaN(newSliderValue)) {
      return;
    }

    if (safeMax === safeMin) {
      onChange(safeMin);
      return;
    }

    const ratio = (newSliderValue - SLIDER_MIN) / (SLIDER_MAX - SLIDER_MIN || 1);
    const logValue = logMin + ratio * (logMax - logMin);
    const rawValue = Math.pow(10, logValue) - logOffset;
    const clamped = clamp(rawValue, safeMin, safeMax);

    const stepped =
      step && step > 0
        ? Math.round(clamped / step) * step
        : clamped;

    const rounded = Number.isFinite(stepped)
      ? Number(stepped.toFixed(precision))
      : safeMin;

    onChange(clamp(rounded, safeMin, safeMax));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value === '' ? safeMin : Number(event.target.value);
    if (Number.isNaN(newValue)) {
      return;
    }

    const clamped = clamp(newValue, safeMin, safeMax);
    const stepped =
      step && step > 0
        ? Math.round(clamped / step) * step
        : clamped;
    const rounded = Number.isFinite(stepped)
      ? Number(stepped.toFixed(precision))
      : safeMin;

    onChange(clamp(rounded, safeMin, safeMax));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label htmlFor={id} className="block text-sm font-semibold text-gray-700">
          {label}
        </label>
        <span className="text-sm font-semibold text-gray-700">
          {formatDisplay(clamp(value, safeMin, safeMax))}
        </span>
      </div>

      <div className="flex items-center gap-4">
        <input
          type="range"
          min={SLIDER_MIN}
          max={SLIDER_MAX}
          step={SLIDER_STEP}
          value={sliderValue}
          onChange={handleSliderChange}
          aria-label={`${label} slider`}
          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#2cc3d1]"
        />

        <div className="flex items-center gap-1">
          {prefix && <span className="text-sm text-gray-500">{prefix}</span>}
          <input
            id={id}
            type="number"
            value={Number(clamp(value, safeMin, safeMax)).toFixed(precision)}
            onChange={handleInputChange}
            step={step}
            min={safeMin}
            max={safeMax}
            className="w-24 px-3 py-1.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2cc3d1] focus:border-transparent text-sm"
          />
          {suffix && <span className="text-sm text-gray-500">{suffix}</span>}
        </div>
      </div>

      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>{minLabel ?? formatDisplay(safeMin)}</span>
        <span>{maxLabel ?? formatDisplay(safeMax)}</span>
      </div>

      {helpText && <p className="text-xs text-gray-500 mt-2">{helpText}</p>}
    </div>
  );
}

export default DualInputSlider;
